"use client";

import { serverURL } from "@/app/ApiConfig";
import UserContext from "@/context/UserContext";
import { AlertError, AlertSuccess } from "@/components/Alerts/Alerts";
import ToastAlertContainer from "@/components/Alerts/toastContainer";
import { submitButtonDisabled } from "@/components/Buttons/Button";
import InputField from "@/components/Input/InputField";
import { EnvelopeIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import { useForm } from "laravel-precognition-react";
import Image from "next/image";
import Link from "next/link";
import { useContext, useState } from "react";

function EmailConfirmation() {
  const [dataSubmitting, setDataSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const user = useContext(UserContext);
  const [emailData, setEmailData] = useState({
    email: "",
  });

  const form = useForm("post", `${serverURL}/forgot-password`, {
    email: "",
  });

  const handleChange = (event: { target: { name: any; value: any } }) => {
    const { name, value } = event.target;

    setEmailData((prevemailData) => ({
      ...prevemailData,
      [name]: value,
    }));
  };
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (emailData) {
        const response = await axios.post("/api/forgot-password", {
          email: emailData.email,
        });

        // Handle success
        AlertSuccess("Email sent successfully");

        // Clear form fields
        setEmailData({
          email: "",
        });
      }
    } catch (error) {
      AlertError("An error occurred while sending email");
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex justify-center items-center min-h-screen px-6 lg:px-8 bg-gradient-to-br from-green-50 via-white to-green-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="w-full max-w-md rounded-2xl backdrop-blur-lg bg-white/80 dark:bg-gray-900/70 shadow-xl ring-1 ring-black/10 dark:ring-white/10 p-6 sm:p-8">
        {/* Logo */}
        <div className="sm:mx-auto sm:w-full sm:max-w-sm text-center">
          <Image
            className="h-16 w-auto mx-auto drop-shadow-md"
            src="/mzunilogo.png"
            alt="Mzuzu University"
            width={160}
            height={160}
          />
          <h2 className="mt-4 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Reset your password
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Enter the email address you used to register with.
          </p>
        </div>

        {/* Form */}
        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Input */}
            <InputField
              id="email"
              name="email"
              type="email"
              value={emailData?.email}
              onBlur={() => form.validate("email")}
              invalid={form.invalid("email")}
              onChange={handleChange}
              placeholder="Enter email address"
              required
              inputIcon={
                <EnvelopeIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              }
              label="Email"
            />
            <div className="mt-1">
              <Link
                href=""
                className="text-xs sm:text-sm text-green-600 dark:text-green-400 hover:underline"
              >
                Forgot or lost your email address?
              </Link>
            </div>

            {/* Buttons */}
            <div className="flex justify-between items-center">
              {/* Back button (desktop) */}
              <Link
                href="/login"
                className="hidden sm:inline-flex items-center rounded-md bg-gray-200 dark:bg-gray-800 px-3 py-2 text-sm font-medium text-primary hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm transition-transform duration-200 hover:scale-105"
              >
                Back to sign in
              </Link>

              {/* Submit */}
              <div className="flex-1 text-right">
                {!isLoading ? (
                  <button
                    type="submit"
                    disabled={form.hasErrors || dataSubmitting}
                    className={`inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 ${
                      form.hasErrors || dataSubmitting
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:scale-105"
                    }`}
                  >
                    Send instructions
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex items-center rounded-md bg-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300 shadow-sm"
                  >
                    <svg
                      width="20"
                      height="20"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-2 animate-spin"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9.5"
                        fill="none"
                        strokeWidth="3"
                        className="opacity-25"
                      />
                      <path
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        className="opacity-75"
                      />
                    </svg>
                    Processing
                  </button>
                )}
              </div>

              {/* Back button (mobile) */}
              <Link
                href="/login"
                className="sm:hidden inline-flex items-center rounded-md bg-gray-200 dark:bg-gray-800 px-3 py-2 text-sm font-medium text-primary hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm transition-transform duration-200 hover:scale-105"
              >
                Back
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Toast container */}
      <ToastAlertContainer />
    </div>
  );
}

export default EmailConfirmation;
